import React, { useEffect, useState, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  setWorkshopData,
  setSubmitData,
  setEventInfo,
} from "@store/eventForm/eventFormSlice";
import { setFormStep } from "@store/ui/formStepSlice";
import "@styles/pages/EventForm/step1/FormPage.scss";
import {
  Box,
  MenuItem,
  Select,
  Typography,
  TextField,
  useMediaQuery,
  Checkbox,
  Button,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import { useForm, Controller, useWatch } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { EventFormValidationSchema } from "@models/ValidationSchema";
import { Trans, useTranslation } from "react-i18next";
import { getWorkShopList, checkDuplcateSubmission } from "@api/formApi";
import { useSearchParams } from "react-router-dom";
import { countriesData } from "@data/GlobalData";
import { getDisplayTitleAndImage } from "@utils/getDisplayTitleAndImage";
import { toast } from "react-toastify";
import Popup from "reactjs-popup";

const FormPage = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();

  const memberInfoData = useSelector(
    (state) => state.memberInfo.memberInfoData
  );
  const workshopData = useSelector((state) => state.eventForm.workshopData);
  const submitData = useSelector((state) => state.eventForm.submitData);
  const eventInfo = useSelector((state) => state.eventForm.eventInfo);

  const isMobile = useMediaQuery("(max-width:768px)");
  const isChinese = i18n.language === "hk";

  const [searchParams] = useSearchParams();
  const code = searchParams.get("code") || "";
  const workshopCode = searchParams.get("workshop_code") || "";
  const id = searchParams.get("id") || "";

  const params = {
    ...(code && { code }),
    ...(workshopCode && { workshop_code: workshopCode }),
    ...(id && { id }),
  };

  const action = code || id ? "submit" : workshopCode ? "walk_in" : "";

  useEffect(() => {
    // code = "preview"
    // workshopcode = "walk_in"

    action
      ? dispatch(setSubmitData({ ...submitData, action }))
      : dispatch(setSubmitData({ ...submitData, action: "submit" }));
  }, []);

  const [isLoading, setIsLoading] = useState(false);
  const [openPopup, setOpenPopup] = useState(false);
  const [haveWorkshopCode, setHaveWorkshopCode] = useState(false);
  const [registeredData, setRegisteredData] = useState([]);
  const hasMemberInfo = !!memberInfoData;
  const [currentFormStatusCode, setCurrentFormStatusCode] = useState(null);
  const {
    control,
    watch,
    setValue,
    getValues,
    resetField,
    reset,
    setFocus,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(
      EventFormValidationSchema(hasMemberInfo, memberInfoData)
    ),
    defaultValues: {
      select: "852",
    },
  });

  useEffect(() => {
    workshopCode ? setHaveWorkshopCode(true) : "";
  }, []);

  const onError = (errs) => {
    const first = Object.keys(errs)[0];
    if (!first) return;

    // 先試原生 input/textarea/select（大多數欄位都覆蓋到）
    let el =
      document.querySelector(`input[name="${first}"]`) ||
      document.querySelector(`textarea[name="${first}"]`) ||
      document.querySelector(`select[name="${first}"]`);

    // 兼容 react-select（你有用 classNamePrefix）
    if (!el) {
      // 國碼下拉（countries）
      el = document.querySelector(".dropdown-class-countries__control");
    }
    if (!el && first === "purchaseChannel") {
      // 購買渠道下拉（channels）
      el = document.querySelector(".dropdown-class-channels__control");
    }

    // 兼容 datepicker（用你嘅 .datepicker class）
    if (!el && first === "purchaseDate") {
      const dp = document.querySelector(".datepicker");
      // 盡量抓到內層真正 input（react-datepicker 會有 input）
      el =
        dp?.querySelector("input") ||
        document.querySelector(`input[name="${first}"]`) ||
        dp;
    }

    // 滾到視窗中間（如有 fixed header，見下方 CSS）
    el?.scrollIntoView({ behavior: "smooth", block: "center" });

    // 原生欄位可直接 setFocus（非原生就算唔 focus，都已經滾到位）
    try {
      setFocus(first, { shouldSelect: true });
    } catch {}
  };

  const displayData = useMemo(() => {
    return getDisplayTitleAndImage(eventInfo, isChinese, isMobile);
  }, [eventInfo, i18n.language, isMobile]);

  useEffect(() => {
    const fetchWorkShopList = async () => {
      try {
        const res = await getWorkShopList(params);
        console.log(res, "fetchWorkShopList");
        const data = res.data.Data;
        const statusCode = res.data.StatusCode;

        switch (statusCode) {
          case 200:
            dispatch(setWorkshopData(data));
            if (data.length > 0) {
              const targetId = id ? Number(id) : data[0].id;
              const targetEvent = data.find((item) => item.id === targetId);
              console.log(targetEvent?.id);
              dispatch(setEventInfo(targetEvent));

              // ✅ 如果之前有填過資料，優先 reset 用戶填過的
              console.log(submitData);
              console.log(Object.keys(submitData).length);
              if (submitData && Object.keys(submitData).length > 1) {
                reset({
                  ...submitData,
                  tnc: submitData.tnc ?? false, // fallback to false
                  promotion: submitData.promotion ?? false, // fallback to false
                  event_name: Number(submitData.event_name),
                  event_time: Number(submitData.event_time),
                });

                // ⬇️ 補這段：設定 eventInfo，否則 location/date/time 無法出現 label
                const selected = data.find(
                  (e) => e.id === submitData.event_name
                );
                if (selected) dispatch(setEventInfo(selected));
              } else {
                reset({
                  event_name: targetEvent?.id,
                  select: "852",
                });
                dispatch(setEventInfo(targetEvent)); // 初始化
                console.log(getValues("event_name"), "-.-");
              }
            }
            break;
          case 701:
            console.log("701");
            setCurrentFormStatusCode(701);
            toast.error(t("status_message:status_code_701"));
            break;
        }
      } catch (err) {
        console.error(err);
      }
    };
    fetchWorkShopList();
  }, []);

  const handlecheckDuplicateClick = async () => {
    setIsLoading(true);
    const finalData = {
      workshop_detail_id: getValues("event_time"),
      email: getValues("email"),
      phone: getValues("select") + getValues("mobile"),
    };

    try {
      const res = await checkDuplcateSubmission(finalData);
      const statusCode = res.data.StatusCode;

      switch (statusCode) {
        case 200:
          handleSubmit(onSubmit)();
          break;
        case 703:
          if (haveWorkshopCode) {
            setRegisteredData(res.data.Data);
            setOpenPopup(true);
          } else {
            toast.error(t("status_message:status_code_703"));
          }

          break;
        case 308:
          toast.error(t("status_message:status_code_308"));
          break;
      }
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const onSubmit = (d) => {
    const additionalData = memberInfoData
      ? {
          last_name: memberInfoData.last_name,
          first_name: memberInfoData.first_name,
          phone: memberInfoData.phone,
          email: memberInfoData.email,
        }
      : {};
    console.log(d);

    dispatch(setSubmitData({ ...submitData, ...d, ...additionalData }));

    dispatch(setFormStep({ formKey: "eventForm", step: 1 }));
  };

  return (
    <Box className="form-page-container">
      {currentFormStatusCode == 701 ? (
        <Typography className="title">
          <Trans i18nKey="status_message:status_code_701" />
        </Typography>
      ) : (
       <>
      {displayData.image && (
        <img
          src={`${process.env.REACT_APP_URL_DOMAIN}/${displayData.image}`}
          className="image"
          alt="Event"
        />
      )}
      <Typography className="title">{displayData.title}</Typography>
      <Typography className="description">{displayData.description}</Typography>
      <Typography className="form-notice" variant="body2">
        <span
          dangerouslySetInnerHTML={{
            __html: t("form:form_notice"),
          }}
        />
      </Typography>
      <Typography className="fields-marked" variant="body2">
        <span
          dangerouslySetInnerHTML={{
            __html: t("form:fields_marked"),
          }}
        />
      </Typography>
      <Box className="row-container">
        {/* Last Name */}
        <Box className="field-container">
          <Typography>
            <span className="red">*</span>
            <Trans i18nKey="form:last_name" />
          </Typography>
          {memberInfoData ? (
            <Box className="member-info-fields">
              <Typography>{memberInfoData?.last_name}</Typography>
            </Box>
          ) : (
            <Controller
              name="last_name"
              control={control}
              render={({ field }) => (
                <>
                  <TextField
                    {...field}
                    inputRef={field.ref}
                    fullWidth
                    variant="outlined"
                    error={!!errors.last_name}
                  />
                  {errors.last_name && (
                    <Typography className="error-text">
                      <Trans i18nKey="form:last_name_error" />
                    </Typography>
                  )}
                </>
              )}
            />
          )}
        </Box>
        {/* Last Name */}
        {/* First Name */}
        <Box className="field-container">
          <Typography>
            <span className="red">*</span>
            <Trans i18nKey="form:first_name" />
          </Typography>
          {memberInfoData ? (
            <Box className="member-info-fields">
              <Typography>{memberInfoData?.first_name}</Typography>
            </Box>
          ) : (
            <Controller
              name="first_name"
              control={control}
              render={({ field }) => (
                <>
                  <TextField
                    {...field}
                    inputRef={field.ref}
                    fullWidth
                    variant="outlined"
                    error={!!errors.first_name}
                  />
                  {errors.first_name && (
                    <Typography className="error-text">
                      <Trans i18nKey="form:first_name_error" />
                    </Typography>
                  )}
                </>
              )}
            />
          )}
        </Box>
      </Box>
      {/* First Name */}
      {/* Mobile input with country code */}
      <Box className="field-container">
        <Typography>
          <span className="red">*</span>
          <Trans i18nKey="form:mobile_number" />
        </Typography>
        {memberInfoData?.phone ? (
          <Box className="member-info-fields">
            <Typography>{memberInfoData?.phone}</Typography>
          </Box>
        ) : (
          <>
            <Box sx={{ display: "flex" }}>
              <Controller
                name="select"
                //name="select.value"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    className="country-code dropdown-select"
                    variant="outlined"
                    error={!!errors.mobile}
                  >
                    {countriesData.map((item, key) => {
                      return (
                        <MenuItem key={key} value={item.value}>
                          {item.label}
                        </MenuItem>
                      );
                    })}
                  </Select>
                )}
              />
              <Controller
                name="mobile"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    inputRef={field.ref}
                    fullWidth
                    variant="outlined"
                    error={!!errors.mobile}
                  />
                )}
              />
            </Box>
            {errors.mobile && (
              <Typography className="error-text">
                <Trans i18nKey="form:mobile_number_error" />
              </Typography>
            )}
          </>
        )}
      </Box>
      {/* Mobile input with country code */}
      {/* Email address */}
      <Box className="field-container">
        <Typography>
          <span className="red">*</span>
          <Trans i18nKey="form:email_address" />
        </Typography>
        {memberInfoData ? (
          <Box className="member-info-fields">
            <Typography>{memberInfoData.email}</Typography>
          </Box>
        ) : (
          <Controller
            name="email"
            control={control}
            render={({ field }) => (
              <>
                <TextField
                  {...field}
                  inputRef={field.ref}
                  fullWidth
                  variant="outlined"
                  error={!!errors.email}
                />
                {errors.email && (
                  <Typography className="error-text">
                    <Trans i18nKey="form:email_address_error" />
                  </Typography>
                )}
              </>
            )}
          />
        )}
      </Box>
      {/* Email address */}
      {/* Event Name */}
      <Box className="field-container">
        <Typography>
          <span className="red">*</span>
          <Trans i18nKey="form:event_name" />
        </Typography>
        <Controller
          name="event_name"
          control={control}
          render={({ field }) => {
            return (
              <>
                <Select
                  sx={{ width: "100%", textAlign: "center" }}
                  {...field}
                  inputRef={field.ref}
                  value={field.value || ""}
                  error={!!errors.event_name}
                  onChange={(e) => {
                    const selectedId = e.target.value;
                    const selected = workshopData.find(
                      (item) => item.id === selectedId
                    );
                    dispatch(setEventInfo(selected));
                    field.onChange(selectedId); // 儲 id
                    resetField("event_location", { defaultValue: "" });
                    resetField("event_date", { defaultValue: "" });
                    resetField("event_time", { defaultValue: "" });
                    setValue("event_location", "");
                    setValue("event_date", "");
                    setValue("event_time", "");
                  }}
                  displayEmpty
                >
                  {workshopData.map((item) => (
                    <MenuItem
                      key={item.id}
                      value={item.id}
                      sx={{ display: "block", textAlign: "center" }}
                    >
                      {isChinese ? item.title_hk : item.title_hk_en}
                    </MenuItem>
                  ))}
                </Select>{" "}
                {errors.event_name && (
                  <Typography className="error-text">
                    <Trans i18nKey="form:event_name_error" />
                  </Typography>
                )}
              </>
            );
          }}
        />
      </Box>
      {/* Event Location */}
      <Box className="field-container">
        <Typography>
          <span className="red">*</span>
          <Trans i18nKey="form:event_location" />
        </Typography>
        <Controller
          name="event_location"
          control={control}
          render={({ field }) => {
            const event = workshopData.find(
              (e) => e.id === getValues("event_name")
            );
            const locations = event?.workshop_location || [];
            return (
              <>
                <Select
                  sx={{ width: "100%", textAlign: "center" }}
                  {...field}
                  inputRef={field.ref}
                  value={field.value || ""}
                  disabled={!event}
                  error={!!errors.event_date}
                  onChange={(e) => {
                    const selectedValue = e.target.value;
                    field.onChange(selectedValue);
                    resetField("event_date", { defaultValue: "" });
                    resetField("event_time", { defaultValue: "" });
                    setValue("event_date", "");
                    setValue("event_time", "");
                  }}
                >
                  {locations.map((item) => (
                    <MenuItem
                      key={item.address_hk_en}
                      value={item.address_hk_en}
                      sx={{ display: "block", textAlign: "center" }}
                    >
                      {isChinese ? item.address_hk : item.address_hk_en}
                    </MenuItem>
                  ))}
                </Select>{" "}
                {errors.event_location && (
                  <Typography className="error-text">
                    <Trans i18nKey="form:event_location_error" />
                  </Typography>
                )}
              </>
            );
          }}
        />
      </Box>
      {/* Event Date */}
      <Box className="field-container">
        <Typography>
          <span className="red">*</span>
          <Trans i18nKey="form:event_date" />
        </Typography>
        <Controller
          name="event_date"
          control={control}
          render={({ field }) => {
            const event = workshopData.find(
              (e) => e.id === getValues("event_name")
            );
            const location = event?.workshop_location?.find(
              (l) => l.address_hk_en === getValues("event_location")
            );
            const dates = location?.workshop_date || [];
            return (
              <>
                <Select
                  sx={{ width: "100%", textAlign: "center" }}
                  {...field}
                  inputRef={field.ref}
                  value={field.value || ""}
                  disabled={!location}
                  error={!!errors.event_date}
                  onChange={(e) => {
                    field.onChange(e.target.value);
                    resetField("event_time", { defaultValue: "" });
                    setValue("event_time", "");
                  }}
                >
                  {dates.map((item) => (
                    <MenuItem
                      key={item.date}
                      value={item.date}
                      sx={{ display: "block", textAlign: "center" }}
                    >
                      {item.date}
                    </MenuItem>
                  ))}
                </Select>{" "}
                {errors.event_date && (
                  <Typography className="error-text">
                    <Trans i18nKey="form:evnet_date_error" />
                  </Typography>
                )}
              </>
            );
          }}
        />
      </Box>
      {/* Event Time */}
      <Box className="field-container">
        <Typography>
          <span className="red">*</span>
          <Trans i18nKey="form:event_time" />
        </Typography>
        <Controller
          name="event_time"
          control={control}
          render={({ field }) => {
            const event = workshopData.find(
              (e) => e.id === getValues("event_name")
            );
            const location = event?.workshop_location?.find(
              (l) => l.address_hk_en === getValues("event_location")
            );
            const dateObj = location?.workshop_date?.find(
              (d) => d.date === getValues("event_date")
            );
            const timeList = dateObj?.workshop_time || [];

            return (
              <>
                <Select
                  sx={{ width: "100%", textAlign: "center" }}
                  {...field}
                  inputRef={field.ref}
                  value={field.value || ""}
                  disabled={!dateObj}
                  error={!!errors.event_time}
                  onChange={(e) => {
                    field.onChange(parseInt(e.target.value, 10)); // store only ID
                  }}
                >
                  {timeList.map((item) => (
                    <MenuItem
                      key={item.workshop_detail_id}
                      value={item.workshop_detail_id}
                      sx={{ display: "block", textAlign: "center" }}
                      disabled={item.quota === 0}
                    >
                      {isChinese ? item.time_slot_hk : item.time_slot_hk_en}
                    </MenuItem>
                  ))}
                </Select>
                {errors.event_time && (
                  <Typography className="error-text">
                    <Trans i18nKey="form:event_time_error" />
                  </Typography>
                )}
              </>
            );
          }}
        />
      </Box>
      {/* Event Time */}
      {/* Tnc */}
      {hasMemberInfo ? (
        <></>
      ) : (
        <>
          <Box className="field-container">
            <Controller
              name="tnc"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <>
                  <Box className="checkbox-container">
                    <Checkbox
                      {...field}
                      inputRef={field.ref}
                      checked={!!field.value}
                      color="primary"
                    />
                    <Typography variant="body2">
                      <span className="red">*</span>
                      <span
                        dangerouslySetInnerHTML={{ __html: t("form:tnc") }}
                      ></span>
                    </Typography>
                  </Box>
                  {errors.tnc && (
                    <Typography className="error-text">
                      <Trans i18nKey="form:tnc_error" />
                    </Typography>
                  )}
                </>
              )}
            />
          </Box>

          {/* Tnc */}
          {/* Promotion */}
          <Box className="field-container">
            <Controller
              name="promotion"
              control={control}
              render={({ field }) => (
                <Box className="checkbox-container">
                  <Checkbox
                    {...field}
                    inputRef={field.ref}
                    checked={!!field.value}
                    color="primary"
                  />
                  <Typography variant="body2">
                    <span
                      dangerouslySetInnerHTML={{ __html: t("form:promotion") }}
                    ></span>
                  </Typography>
                </Box>
              )}
            />
          </Box>
        </>
      )}
      {/* Promotion */}
      {/* Submit */}
      <Box className="next-button-container">
        <LoadingButton
          className="loading-button"
          disabled={isLoading}
          loading={isLoading}
          onClick={handleSubmit(handlecheckDuplicateClick, onError)}
        >
          <Trans i18nKey="form:next_button" />
        </LoadingButton>
        {/* {haveWorkshopCode ? (
       <LoadingButton
            className="loading-button"
            disabled={isLoading}
            loading={isLoading}
            onClick={handleSubmit(handlecheckDuplicateClick, onError)}
          >
            <Trans i18nKey="form:next_button" />
          </LoadingButton>
        ) : (
          <Button
            className="loading-button"
            onClick={handleSubmit(handlecheckDuplicateClick, onError)}
          >
            <Trans i18nKey="form:next_button" />
          </Button>
        )} */}
      </Box>
      {/* Submit */}
      </>
      )}
      {/* Popup 視窗 */}
      <Popup open={openPopup} modal onClose={() => setOpenPopup(false)}>
        {(close) => {
          // 取得目前表單選中的值
          const selectedEventId = getValues("event_name");
          const selectedLocation = getValues("event_location");
          const selectedDate = getValues("event_date");
          const selectedTimeId = getValues("event_time");

          const selectedEvent = workshopData.find(
            (e) => e.id === selectedEventId
          );
          // const locationObj = selectedEvent?.workshop_location?.find(
          //   (l) => l.address_hk_en === selectedLocation
          // );
          // const dateObj = locationObj?.workshop_date?.find(
          //   (d) => d.date === selectedDate
          // );
          // const timeObj = dateObj?.workshop_time?.find(
          //   (t) => t.workshop_detail_id === selectedTimeId
          // );

          return (
            <Box className="popup-container">
              <Typography className="popup-title">
                <Trans i18nKey="form:popup_title" />
              </Typography>
              <Box className="popup-detail">
                <Box className="popup-row">
                  <b>
                    <Trans i18nKey="form:event_name" />:
                  </b>
                  {}
                  {isChinese
                    ? selectedEvent?.title_hk
                    : selectedEvent?.title_hk_en}
                </Box>
                <Box className="popup-row">
                  <b>
                    <Trans i18nKey="form:event_location" />:
                  </b>
                  {isChinese
                    ? registeredData?.location_tc
                    : registeredData?.location_en}
                </Box>
                <Box className="popup-row">
                  <b>
                    <Trans i18nKey="form:event_date" />:
                  </b>
                  {registeredData?.date}
                </Box>
                <Box className="popup-row">
                  <b>
                    <Trans i18nKey="form:event_time" />:
                  </b>
                  {isChinese ? registeredData?.timeslot : registeredData?.timeslot}
                </Box>
              </Box>
              <Box className="popup-button-container">
                <Button
                  className="button"
                  variant="outlined"
                  onClick={handleSubmit(onSubmit, onError)}
                >
                  <Trans i18nKey="form:yes" />
                </Button>
                <Button
                  className="button"
                  variant="outlined"
                  onClick={() => {
                    setOpenPopup(false);
                    close();
                  }}
                >
                  <Trans i18nKey="form:no" />
                </Button>
              </Box>
            </Box>
          );
        }}
      </Popup>
    </Box>
  );
};

export default FormPage;
